<template>
  <div class="ak-pack__item" :class="classNames">
    <slot />
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-pack-item :grow=false :shrink=true></ak-pack-item>
   */
  name: 'AkPackItem',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Controls whether the component will expand.
     */
    grow: Boolean,

    /**
     * @type {Boolean}
     * @description Controls whether the component will shrink.
     */
    shrink: Boolean,

    /**
     * @type {String}
     * @description Controls how to align itself against the parent.
     */
    align: {
      validator: (v) => ['top', 'bottom', 'center'].indexOf(v) >= 0,
      type: String,
      default: undefined,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    /**
     * @returns {String}
     * @description Returns the classes that need to be applied to
     * the pack item
     */
    const classNames = computed(() => {
      return {
        'ak-pack__item--grow': props.grow,
        'ak-pack__item--shrink': props.shrink,
        [`ak-pack__item--${props.align}`]: props.align,
      }
    })

    return {
      classNames,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-pack {
  $self: &;

  &__item {
    &--grow {
      flex-grow: 1;
      flex-basis: 0;
    }
    &--shrink {
      flex-shrink: 0;
    }

    // Aligning of self
    &--top {
      align-self: flex-start;
    }
    &--center {
      align-self: center;
    }
    &--bottom {
      align-self: flex-end;
    }

    // Spacing
    #{$self}--tiny > & {
      padding-left: $spacing-tiny;
    }
    #{$self}--small > & {
      padding-left: $spacing-small;
    }
    #{$self}--normal > & {
      padding-left: $spacing;
    }
    #{$self}--large > & {
      padding-left: $spacing-large;
    }

    &:empty {
      padding-left: 0;
    }
  }
}
</style>
